import { MAX_CONCENTRATIONS } from 'constants/experimentDefaults'
import {
  Box,
  Theme,
  Typography
} from '@mui/material'
import React from 'react'
import { RMEResponse } from 'utils/api'
import DeepcellSlider from '../shared/DeepcellSlider'
import { ExperimentData, ExperimentHandlers } from './types'

interface PrepareSampleProps {
  experimentData: ExperimentData
  handlers: ExperimentHandlers
  metricsResult?: RMEResponse | null
  theme: Theme
}

const PrepareSample: React.FC<PrepareSampleProps> = ({ experimentData, handlers, metricsResult, theme }) => {
  // Use the API calculated value if available, otherwise use the default
  const calculatedVolume = metricsResult?.sample_used_ml || experimentData.minInputSampleVolume
  
  // Set maximum concentration based on experiment goal
  const maxConcentration = MAX_CONCENTRATIONS[experimentData.experimentGoal]
  // Display label: show 6.7e6/ml for sorting modes (even though actual max is 6.666)
  const maxLabel = experimentData.experimentGoal === 'imaging' ? '10e6/ml' : '6.7e6/ml'
  return (
    <Box>
      <Box sx={{
        backgroundColor: theme.palette.info.light,
        p: 2,
        borderRadius: 1,
        mb: 2,
        borderBottom: `1px solid ${'#d0d0d0'}`,
        height: '73px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 400, color: theme.palette.text.primary, fontSize: '18px',lineHeight:'20px' }}>
          Step 2: Prepare Sample
        </Typography>
      </Box>

      {/* Tips for concentration - only show for sorting modes */}
      {(experimentData.experimentGoal === 'purity' || experimentData.experimentGoal === 'yield') && (
        <Box sx={{ px: 1, mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: theme.palette.text.secondary,
              mb: 0.5
            }}
          >
            For higher purity, use a lower concentration to reduce throughput.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: theme.palette.text.secondary
            }}
          >
            For shorter run duration, increase concentration for higher throughput.
          </Typography>
        </Box>
      )}

      <Box sx={{ px: 1 }}>
        <Box sx={{mb:3}}>
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: 400, color: theme.palette.text.primary, fontSize: '16px',lineHeight:'20px' }}
          >
            1. Prepare the sample with this cell concentration:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '16px', lineHeight:'20px'}}
            >
              {experimentData.cellConcentration.toFixed(1)}e6/mL
            </Typography>
            <Box sx={{ width: 200, flex: 1 }}>
              <DeepcellSlider
                value={experimentData.cellConcentration}
                onChange={handlers.handleCellConcentrationChange}
                min={0.3}
                max={maxConcentration}
                step={0.1}
                valueLabelDisplay="off"
                showRangeLabels
                minLabel="0.3e6/ml"
                maxLabel={maxLabel}
                color={theme.palette.primary.main}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: 400, color: theme.palette.text.primary, fontSize: '16px',lineHeight:'20px' }}
          >
            2. And with minimum input sample volume of
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '16px',lineHeight:'20px' }}
            >
              {calculatedVolume.toFixed(3)} mL
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PrepareSample
